import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Grid, LinearProgress, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import apiClient from '../../ApiClient/ApiClient';
import { recognizeDemandImage } from './productionOcr';
import { parseOcrDemand } from './demandImageParser';

export default function DemandImportDialog({ open, onClose, onApply, companies = [] }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ status: '', progress: 0 });
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [rawText, setRawText] = useState('');
  const [keyword, setKeyword] = useState('');
  const [sheet, setSheet] = useState('');
  const [page, setPage] = useState(0);
  const operation = useRef(null);
  const company = sessionStorage.getItem('company');

  useEffect(() => {
    if (!open) return undefined;
    setItems([]); setSelected(null); setConfirmed(false); setError(''); setImage(null); setRawText(''); setSummary(''); setKeyword(''); setSheet(''); setPage(0);
    return () => operation.current?.abort();
  }, [open]);
  useEffect(() => {
    if (!image) { setImageUrl(''); return undefined; }
    const url = URL.createObjectURL(image); setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const load = async (file, type) => {
    if (!file || busy) return;
    const controller = new AbortController(); operation.current = controller;
    setBusy(true); setError(''); setSelected(null); setItems([]); setConfirmed(false); setRawText(''); setSummary(''); setKeyword(''); setSheet(''); setPage(0);
    try {
      const extension = file.name.split('.').pop().toLowerCase();
      if (file.size > (type === 'Excel' ? 20 : 10) * 1024 * 1024) throw new Error(type === 'Excel' ? 'Excel 不可超過 20 MB。' : '圖片不可超過 10 MB。');
      if (type === 'Excel') {
        setImage(null);
        if (!['xls', 'xlsx'].includes(extension)) throw new Error('僅支援 .xls、.xlsx。');
        const body = new FormData(); body.append('file', file);
        const response = await apiClient.post('/ProductionReport/PreviewDemand', body, { signal: controller.signal });
        if (!response.data?.success) throw new Error(response.data?.message || '讀取失敗');
        if (controller.signal.aborted) return;
        const preview = response.data.data; setItems(preview.items);
        setSummary(`共 ${preview.items.length} 筆，可預覽帶入 ${preview.eligible} 筆；其他公司 ${preview.otherCompany} 筆，異常 ${preview.invalid} 筆。僅預覽，尚未儲存報工。`);
      } else {
        if (!['png', 'jpg', 'jpeg', 'webp'].includes(extension)) throw new Error('請使用 PNG、JPG 或 WebP 圖片。');
        setImage(file);
        const bitmap = await createImageBitmap(file);
        const pixels = bitmap.width * bitmap.height; bitmap.close();
        if (pixels > 24000000) throw new Error('圖片超過 2,400 萬像素，請先縮小或裁切單張需求單。');
        setProgress({ status: '載入本機辨識引擎', progress: 0 });
        const data = await recognizeDemandImage(file, setProgress, controller.signal);
        if (controller.signal.aborted) return;
        setRawText(data.text); setItems(parseOcrDemand(data, file.name, companies));
        setSummary('辨識完成。請對照圖片逐欄修正，確認公司、單號、項次、圖號與需求數量。');
      }
    } catch (exception) {
      if (!controller.signal.aborted) setError(exception.response?.data?.message || exception.message || '處理失敗，請重新選擇檔案。');
    } finally { if (operation.current === controller) { operation.current = null; setBusy(false); } }
  };
  const filtered = useMemo(() => items.filter((row) => (!sheet || row.fields.sourceSheet === sheet)
    && `${row.fields.sourceDocumentNo} ${row.fields.drawingNo} ${row.fields.productionBatchNo} ${row.fields.customerName}`.toLowerCase().includes(keyword.toLowerCase())), [items, sheet, keyword]);
  const sheets = [...new Set(items.map((row) => row.fields.sourceSheet).filter(Boolean))];
  const choose = (row) => {
    setSelected({ ...row.fields, plannedDeliveryDate: row.fields.plannedDeliveryDate?.slice(0, 10) || '', demandQuantity: row.fields.demandQuantity ?? '' });
    setConfirmed(false); setError('');
  };
  const change = (key, value) => { setSelected((old) => ({ ...old, [key]: value })); setConfirmed(false); };
  const wrongCompany = selected?.sourceCompanyCode && selected.sourceCompanyCode !== company;
  const valid = selected && selected.sourceDocumentNo.trim() && selected.sourceLineNo.trim() && selected.drawingNo.trim()
    && selected.demandQuantity !== '' && /^\d+(\.\d{1,4})?$/.test(String(selected.demandQuantity)) && Number(selected.demandQuantity) <= 9999999999;
  const apply = () => {
    if (!valid || !confirmed || wrongCompany) return;
    onApply({ ...selected, demandQuantity: Number(selected.demandQuantity), plannedDeliveryDate: selected.plannedDeliveryDate || null,
      sourceCompanyCode: selected.sourceCompanyCode || company, sourceConfirmed: true });
  };
  const picker = (label, type, capture = false) => <Button component="label" variant={type === 'Excel' ? 'contained' : 'outlined'} disabled={busy}>
    {label}<input hidden type="file" accept={type === 'Excel' ? '.xls,.xlsx' : 'image/png,image/jpeg,image/webp'}
      capture={capture ? 'environment' : undefined} onChange={(event) => { load(event.target.files?.[0], type); event.target.value = ''; }} />
  </Button>;
  const fields = [
    ['需求單號', 'sourceDocumentNo', 100], ['來源項次', 'sourceLineNo', 30], ['客戶名稱', 'customerName', 200],
    ['機種', 'model', 100], ['成品圖號', 'drawingNo', 100], ['品名', 'itemName', 200],
    ['需求數量', 'demandQuantity', undefined, 'number'], ['預定交貨日', 'plannedDeliveryDate', undefined, 'date'],
    ['原始交貨日文字', 'deliveryDateText', 300], ['生產批號', 'productionBatchNo', 100], ['需求備註', 'demandRemark', 1000],
  ];

  return <Dialog open={open} onClose={() => { if (!busy) onClose(); }} fullWidth maxWidth="xl">
    <DialogTitle>從生產需求單帶入</DialogTitle>
    <DialogContent dividers>
      <Alert severity="info" sx={{ mb: 2 }}>Excel 或照片只帶入需求資料。需求數量不會填入良品／不良數量，確認後仍需填寫本次實際報工再儲存。照片在此裝置辨識，不上傳圖片。</Alert>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {picker('上傳 Excel', 'Excel')}{picker('上傳圖片辨識', 'Image')}{picker('拍照辨識', 'Image', true)}
        {busy && <Button color="error" onClick={() => operation.current?.abort()}>取消處理</Button>}
      </Stack>
      {busy && <Box sx={{ mb: 2 }}><Typography>{image ? `${progress.status} ${progress.progress}%` : '讀取需求單中…'}</Typography><LinearProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {summary && <Alert severity="warning" sx={{ mb: 2 }}>{summary}</Alert>}
      {imageUrl && <Box sx={{ mb: 2 }}><img src={imageUrl} alt="原始生產需求單" style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }} /></Box>}
      {rawText && <details><summary>查看辨識原文</summary><Typography component="pre" sx={{ whiteSpace: 'pre-wrap', maxHeight: 250, overflow: 'auto' }}>{rawText}</Typography></details>}
      {items.length > 0 && <>
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <TextField size="small" label="搜尋單號／圖號／批號／客戶" value={keyword} onChange={(event) => { setKeyword(event.target.value); setPage(0); }} />
          {sheets.length > 0 && <TextField select size="small" label="工作表" value={sheet} sx={{ minWidth: 180 }} onChange={(event) => { setSheet(event.target.value); setPage(0); }}>
            <MenuItem value="">全部</MenuItem>{sheets.map((name) => <MenuItem value={name} key={name}>{name}</MenuItem>)}
          </TextField>}
        </Stack>
        <Box sx={{ overflowX: 'auto', maxHeight: 350 }}><Table size="small" stickyHeader>
          <TableHead><TableRow>{['選取', '工作表／列', '來源公司', '需求單號', '項次', '圖號', '品名', '需求數量', '交貨日', '批號', '檢查訊息'].map((name) => <TableCell key={name}>{name}</TableCell>)}</TableRow></TableHead>
          <TableBody>{filtered.slice(page * 100, (page + 1) * 100).map((row, i) => <TableRow key={`${page}-${i}`}>
            <TableCell><Button disabled={busy || Boolean(row.fields.sourceCompanyCode && row.fields.sourceCompanyCode !== company)} onClick={() => choose(row)}>選取明細</Button></TableCell>
            <TableCell>{row.fields.sourceSheet}／{row.fields.sourceRow}</TableCell><TableCell>{row.sourceCompanyName || row.fields.sourceCompanyCode || '待確認'}</TableCell>
            <TableCell>{row.fields.sourceDocumentNo}</TableCell><TableCell>{row.fields.sourceLineNo}</TableCell><TableCell>{row.fields.drawingNo}</TableCell><TableCell>{row.fields.itemName}</TableCell>
            <TableCell>{row.fields.demandQuantity ?? '待確認'}</TableCell><TableCell>{row.fields.deliveryDateText}</TableCell><TableCell>{row.fields.productionBatchNo}</TableCell>
            <TableCell sx={{ minWidth: 200 }}>{[...row.errors, ...row.warnings].join('；')}</TableCell>
          </TableRow>)}</TableBody>
        </Table></Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center"><Typography>共 {filtered.length} 筆，第 {page + 1} 頁</Typography>
          <Button disabled={page === 0} onClick={() => setPage((x) => x - 1)}>上一頁</Button><Button disabled={(page + 1) * 100 >= filtered.length} onClick={() => setPage((x) => x + 1)}>下一頁</Button></Stack>
      </>}
      {selected && <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>確認並修正選取的明細</Typography>
        {wrongCompany && <Alert severity="error">文件公司與登入公司不符，不可帶入。</Alert>}
        <Grid container spacing={2}>{fields.map(([label, key, maxLength, type = 'text']) => <Grid item xs={12} sm={6} md={4} key={key}>
          <TextField fullWidth size="small" label={label} type={type} value={selected[key]} disabled={busy}
            required={['sourceDocumentNo', 'sourceLineNo', 'drawingNo', 'demandQuantity'].includes(key)}
            inputProps={type === 'number' ? { min: 0, step: '0.0001' } : { maxLength }} InputLabelProps={type === 'date' ? { shrink: true } : undefined}
            onChange={(event) => change(key, event.target.value)} />
        </Grid>)}</Grid>
        <FormControlLabel control={<Checkbox checked={confirmed} disabled={busy || Boolean(wrongCompany)} onChange={(event) => setConfirmed(event.target.checked)} />}
          label={`我已核對單號、圖號、數量及交貨日，並確認此需求屬於目前登入公司 ${company}。`} />
      </Box>}
    </DialogContent>
    <DialogActions><Button disabled={busy} onClick={onClose}>關閉</Button>
      <Button variant="contained" disabled={busy || !valid || !confirmed || Boolean(wrongCompany)} onClick={apply}>確認帶入報工表單</Button></DialogActions>
  </Dialog>;
}

DemandImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  companies: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, name: PropTypes.string })),
};
